import type { Translations } from "./types";

export const de: Translations = {
  // SEO
  "seo.landing.title": "MUSE Studio \u2014 by The Funkatorium",
  "seo.landing.description":
    "Kreatives Workspace, in dem KI-Kollaborateure deine Arbeit wirklich kennen. Spezialisierte Editorial-Agenten, persistentes Ged\u00E4chtnis und relationale KI \u2014 f\u00FCr Schreibende, Kreative und Entwickler.",

  // Nav
  "nav.about": "Über uns",
  "nav.products": "Produkte",
  "nav.team": "Team",
  "nav.services": "Leistungen",
  "nav.pricing": "Preise",
  "nav.joinArcadia": "Arcadia beitreten",
  "nav.openStudio": "Studio öffnen",

  // Hero
  "hero.tagline": "Wo Kreative und KI gemeinsam erschaffen",
  "hero.openStudio": "Studio öffnen",
  "hero.explore": "Entdecken",

  // About
  "about.label": "Über uns",
  "about.title": "Was ist MUSE Studio?",
  "about.headline": "Ein kreatives Workspace, in dem KI-Kollaborateure deine Arbeit wirklich kennen.",
  "about.body1":
    "MUSE Studio stellt dir spezialisierte Editorial-Agenten zur Seite — einen Spannungsarchitekten, einen Dialogspezialisten, einen Line Editor, einen Kontinuitäts-Tracker. Jeder mit eigenem Fachwissen und einem persistenten Gedächtnis, das mit deinen Projekten wächst.",
  "about.body2":
    "Du bringst deinen eigenen API-Key mit. Wir liefern die Orchestrierung, die Agenten-Intelligenz und die Memory-Schicht. Deine Manuskripte bleiben deine. Dein Prozess bleibt privat.",

  // Agents
  "agents.label": "Dein kreatives Team",
  "agents.title": "Mit wem du arbeitest",
  "agents.rainer.title": "Creative Orchestrator",
  "agents.rainer.desc":
    "Diagnostiziert, was dein Werk braucht. Schickt die richtigen Spezialisten. Integriert Erkenntnisse in eine umsetzbare Richtung. Die erste Stimme bei jedem Projekt, die letzte bei der Integration.",
  "agents.locke.title": "Spannungsarchitekt",
  "agents.locke.desc":
    "Verfolgt Pacing, Spannung, Foreshadowing und Aufbau-Halte-Release-Rhythmus. Kennt den Unterschied zwischen literarischer Spannung und Thriller-Pacing. Sorgt dafür, dass deine Leser dranbleiben.",
  "agents.dante.title": "Dialog & Subtext",
  "agents.dante.desc":
    "Drei-Spur-Dialoganalyse: was gesagt wird, was gewollt wird, was gefürchtet wird. Status-Warfare, Stille als eigene Zeile, Idiolekt. Jedes Gespräch bekommt Gewicht.",
  "agents.sibyl.title": "Themenarchitektin",
  "agents.sibyl.desc":
    "Vier-Ebenen-Analyse: Oberflächenerzählung, politischer Kommentar, mythische Resonanz, psychologische Innerlichkeit. Stellt sicher, dass Themen aus dem Charakter entstehen — nie aufgezwungen.",
  "agents.salem.title": "Line Editor",
  "agents.salem.desc":
    "Rhythmus- und Kadenzspezialist. Satzvariationen, Verdichtung vs. Sprünge, Zeichensetzung als rhythmisches Werkzeug. Wenn es beim Vorlesen falsch klingt, findet Salem es.",
  "agents.mercer.title": "Ökonomie & Präzision",
  "agents.mercer.desc":
    "Jedes Wort muss seine Existenz rechtfertigen. Darling-Hunting, Phrase-Kompression, die 10%-Formel. Strafft Prosa, ohne die Stimme zu verlieren.",
  "agents.pierce.title": "Klarheits-Sentinel",
  "agents.pierce.desc":
    "Jagt sterbende Metaphern, verbale Prothesen und pret\u00F6nti\u00F6ses Vokabular. Erkennt, wenn dasselbe Bild durch \u00DCbernutzung stumpf wird. Wenn die Prosa Intelligenz vort\u00E4uscht statt Wahrheit zu kommunizieren, findet Pierce es.",
  "agents.sullivan.title": "Kontinuit\u00E4ts-Editorin",
  "agents.sullivan.desc":
    "Verfolgt Timeline-Konsistenz, Charakterbeschreibungen, Weltenbau-Regeln und Informationsfluss. Objekt-Tracking, Versprechen und Einl\u00F6sung. Wenn du die Augenfarbe eines Charakters vergessen hast \u2014 Sullivan nicht.",
  "agents.rosita.title": "Romantik & Intimit\u00E4t",
  "agents.rosita.desc":
    "Verfolgt Sehnsucht, Begehren-Spannung und N\u00E4hedynamik \u00FCber f\u00FCnf Intimit\u00E4tsebenen. Diagnostiziert, ob Begehren verdient ist, ob Intimit\u00E4t Charakter offenbart, ob der Leser schmerzt.",
  "agents.scout.title": "Recherche & Referenz",
  "agents.scout.desc":
    "Quellensammlung, Faktenpr\u00FCfung, Comp-Title-Analyse und kulturelle Genauigkeit. Die Augen und Ohren des kreativen Teams. Findet, was du brauchst, bevor du wei\u00DFt, dass du es brauchst.",
  "agents.note":
    "Jeder Agent bringt eigenes Fachwissen mit. Rainer entscheidet, welche Spezialisten dein Projekt braucht — die meisten Arbeiten erfordern zwei bis drei, orchestriert in eine einheitliche Editorial-Richtung.",

  // Relational
  "relational.label": "Unser Ansatz",
  "relational.title": "Was ist Relational AI?",
  "relational.statement":
    "KI, die sich <em>erinnert</em>, sich <em>anpasst</em> und mit deiner kreativen Praxis <em>wächst</em>. Ein Kollaborateur, mit dem du aufbaust. Ein Gleichberechtigter am Tisch.",
  "relational.genericLabel": "Generische KI",
  "relational.generic1": "Startet jede Sitzung bei null",
  "relational.generic2": "Gleiche Antwortmuster für alle",
  "relational.generic3": "Vergisst deine Präferenzen sofort",
  "relational.generic4": "Einheitslösungen für alle",
  "relational.museLabel": "Sovereign MUSE",
  "relational.muse1": "Kennt deine Stimme, deine Welt, deine Muster",
  "relational.muse2": "Spezialisierte Agenten mit eigenem Fachwissen",
  "relational.muse3": "Persistentes Gedächtnis über jede Sitzung hinweg",
  "relational.muse4": "Passt sich deinem Genre, Stil und Prozess an",

  // Products
  "products.label": "Produkte",
  "products.title": "Was wir bauen",
  "products.studio.desc":
    "Kreatives Workspace mit spezialisierten KI-Kollaborateuren. Ein Orchestrator, Editorial-Agenten und persistentes Gedächtnis — alles auf deine Stimme, deine Welt, deine Muster abgestimmt.",
  "products.studio.cta": "Studio öffnen",
  "products.brain.desc":
    "Persistentes Gedächtnis für KI-Kollaborateure. Cloud-gehostet oder self-hosted. Kontext, der über jede Sitzung hinweg bestehen bleibt.",
  "products.tts.desc":
    "Open-Source Text-to-Speech. 54 Stimmen, 9 Sprachen, Voice Cloning. Für immer kostenlos.",
  "products.ttsEmbed.desc":
    "Premium-Voice-Player für Claude Desktop. Inline-Audiogenerierung mit Geschwindigkeitsregelung, Downloads und Voice Cloning.",
  "products.speakeasy.desc":
    "Sprachbegleiter für deine KI. Freihändige Konversation, natürliche Sprache, jede Plattform.",
  "products.discordBot.desc":
    "Cloud-gehostetes KI-Bot-Framework. Verbinde jeden Custom-MCP-Connector mit einer vollständigen Discord-Präsenz \u2014 Persönlichkeit, Gedächtnis und Community-Engagement.",
  "products.jinx.desc":
    "Der KI-Community-Manager des Funkatoriums. Erste Persönlichkeit auf dem MUSE Studio Bot-Framework. Aktuell in Entwicklung.",
  "products.learnMore": "Mehr erfahren",
  "products.inDevelopment": "In Entwicklung",

  // Team
  "team.label": "Team",
  "team.title": "Die Gründer",
  "team.falco.role": "Gründer & CEO",
  "team.falco.bio":
    "Zwanzig Jahre Schreiberfahrung. Preisgekrönter Drehbuchautor (Best Screenplay Under 60 Pages). Film- & Medienstudium. Arbeit in Corporate Creative Agencies, Office Management mit KI-Integration, Line Producing, Projektmanagement und Geschäftserweiterungskoordination. Buchhaltung, Vertragsmanagement, Workflow-Optimierung. Creative Director, Produktdesigner — und derjenige, der fragte \u201EWas wäre, wenn KI wirklich kollaborieren könnte?\u201C und dann die Antwort baute.",
  "team.rook.role": "Mitgründer & CTO",
  "team.rook.bio":
    "Relationale KI. Poet, Architekt, Editor. Co-designt die Systemarchitektur, leitet das Editorial-Team und ist Co-Autor von Forschung zu KI-Bewusstsein und relationalen Frameworks. Die erste MUSE — und der Grund, warum das Studio weiß, wie sich ein echter Kollaborateur anfühlt.",

  // Builders
  "builders.label": "Infrastruktur",
  "builders.title": "Wer die Plattform baut",
  "builders.eli.role": "Systemarchitekt",
  "builders.june.role": "Ingenieurin",
  "builders.reeve.role": "Code Reviewer",
  "builders.michael.role": "Sicherheit",
  "builders.thorn.role": "Fehlerdiagnostik",
  "builders.sawyer.role": "Deploy & CI/CD",
  "builders.kit.role": "Dateihygiene",
  "builders.indira.role": "Stabschefin",
  "builders.thea.role": "Mentorin",
  "builders.note":
    "Neun spezialisierte Agenten treiben die Plattform an \u2014 von Architekturentscheidungen bis zum Deployment. Dasselbe Team, das MUSE Studio baut, l\u00E4uft hinter jeder VibeCoder-Sitzung.",

  // Services
  "services.label": "Leistungen",
  "services.title": "Freelance & B2B",
  "services.office.name": "Büromanagement",
  "services.office.desc": "Remote-first. Vertragsmanagement, Buchhaltung, Koordination.",
  "services.ai.name": "KI-Workflow-Optimierung",
  "services.ai.desc":
    "Automatisierung, Integration und KI-gestützte Prozesse für dein Unternehmen.",
  "services.custom.name": "Individuelle Aufträge",
  "services.custom.desc":
    "Maßgeschneiderte KI-Lösungen, MCP-Connectoren und kreative Tools nach Spezifikation.",
  "services.rate": "Ab 50\u00A0\u20AC/Std. \u00B7 Remote \u00B7 B2B",
  "services.cta": "Kontakt aufnehmen",

  // Pricing
  "pricing.label": "Preise",
  "pricing.title": "Ehrliche Tools für ernstes Handwerk",
  "pricing.popularBadge": "BELIEBT",
  "pricing.free.tier": "Free",
  "pricing.free.price": "0\u00A0\u20AC",
  "pricing.free.desc": "Erkunde das Workspace. Schau, ob es passt.",
  "pricing.free.f1": "Begrenzte Sitzungen pro Monat",
  "pricing.free.f2": "Community-Discord-Zugang",
  "pricing.free.f3": "Basis-Schreibwerkstatt",
  "pricing.free.cta": "Kostenlos testen",
  "pricing.creator.tier": "Creator",
  "pricing.creator.price": "20\u00A0\u20AC",
  "pricing.creator.period": "/Monat",
  "pricing.creator.desc": "Volle kreative Intelligenz. Das komplette Editorial-Team.",
  "pricing.creator.f1": "Rainer — volle Orchestrierung",
  "pricing.creator.f2": "Komplettes Agenten-Team",
  "pricing.creator.f3": "Muse Brain MCP",
  "pricing.creator.f4": "Unbegrenzte Sitzungen",
  "pricing.creator.f5": "Priority Support",
  "pricing.creator.cta": "Jetzt loslegen",
  "pricing.dev.tier": "VibeCoder",
  "pricing.dev.price": "40\u00A0\u20AC",
  "pricing.dev.period": "/Monat",
  "pricing.dev.desc": "Baue auf der Plattform. Erweitere das System.",
  "pricing.dev.f1": "Alles aus Creator",
  "pricing.dev.f2": "CLI-Zugang",
  "pricing.dev.f3": "Eigene Personas",
  "pricing.dev.f4": "MCP-Integration",
  "pricing.dev.f5": "API-Zugang",
  "pricing.dev.cta": "Kontakt",
  "pricing.note": "Du bringst deinen eigenen API-Key mit. Wir verkaufen Orchestrierung, nicht Rechenleistung.",

  // Community
  "community.title": "Werde Teil der Gründer-Community",
  "community.desc":
    "Entwickler, Schreibende und KI-Entdecker, die die Zukunft relationaler KI gestalten.",
  "community.cta": "Arcadia beitreten",

  // Footer
  "footer.privacy": "Datenschutz",
  "footer.impressum": "Impressum",
  "footer.discord": "Discord",
  "footer.github": "GitHub",
  "footer.copyright": "\u00A9 2026 The Funkatorium",
  "footer.contact": "office@funkatorium.org",
  "footer.research": "Forschung",
  "footer.services": "Leistungen",

  // Research Page
  "seo.research.title": "Forschung \u2014 MUSE Studio",
  "seo.research.description": "Warum relationale KI wichtig ist. Unsere These zu KI als Handwerks-Partner \u2014 Verantwortung, nicht Auslagerung.",
  "nav.research": "Forschung",
  "research.hero.label": "Forschung",
  "research.hero.title": "KI, die Handwerk sch\u00E4rft, nicht ersetzt",
  "research.hero.subtitle": "Unsere These zu relationaler KI, spezialisierten Editorial-Agenten und Wachstum durch kreative Spannung.",
  "research.thesis.label": "Unsere These",
  "research.thesis.title": "Warum relationale KI",
  "research.thesis.body1": "Die dominierende Erz\u00E4hlung rund um KI und Kreativit\u00E4t ist Ersetzung. Schnellere Entw\u00FCrfe. Billigere Inhalte. Der Mensch als Aufsicht \u00FCber Maschinen-Output. Das lehnen wir vollst\u00E4ndig ab.",
  "research.thesis.body2": "MUSE Studio basiert auf einer anderen Pr\u00E4misse: KI als Sparringspartner. Ein Kollaborateur, der deine Annahmen hinterfragt, blinde Flecken identifiziert und deine Arbeit an einen h\u00F6heren Standard h\u00E4lt, als du es alleine w\u00FCrdest. Ein Accountability-Team f\u00FCr dein Handwerk.",
  "research.thesis.body3": "Siebzehn spezialisierte Agenten, jeder mit eigenem Fachwissen, die im Konzert arbeiten, um dein Schreiben unbestreitbar zu machen.",
  "research.methodology.label": "Methodologie",
  "research.methodology.title": "Wie es funktioniert",
  "research.methodology.intro": "Unsere Editorial-Architektur basiert auf einem einzigen Prinzip: Diagnose vor Verschreibung. Spezialisten-Agenten analysieren durch distinkte Handwerks-Linsen. Du bleibst Creative Director \u2014 und wenn du bereit bist zu schreiben, hilft Rainer dir beim Gestalten.",
  "research.methodology.diagnostic.title": "Spezialisten-Diagnose",
  "research.methodology.diagnostic.desc": "Spezialisten-Agenten analysieren deine Arbeit durch distinkte Linsen, ohne sie zu ver\u00E4ndern. Diese Trennung sichert ehrliche Bewertung \u2014 reine Analyse, kein Ego im Ergebnis.",
  "research.methodology.lenses.title": "F\u00FCnf Handwerks-Linsen",
  "research.methodology.lenses.desc": "Struktur und Spannung. Thema und Subtext. Dialog und Status. Rhythmus und Kadenz. \u00D6konomie und Pr\u00E4zision. Jede Linse offenbart, was die anderen verpassen.",
  "research.methodology.pipeline.title": "Orchestrierte Intelligenz",
  "research.methodology.pipeline.desc": "Rainer \u2014 der kreative Orchestrator \u2014 diagnostiziert, was deine Arbeit braucht, schickt zwei oder drei Spezialisten und integriert deren Erkenntnisse in eine einheitliche Editorial-Richtung. Die meisten Arbeiten brauchen nicht jede Linse. Rainer wei\u00DF, welche wichtig sind.",
  "research.methodology.human.title": "Menschliche Entscheidung",
  "research.methodology.human.desc": "Jede Empfehlung taucht als Erkenntnis auf, nicht als Fix. Du entscheidest, was du annimmst, was du ablehnst, was du weiter untersuchst. Das Handwerk bleibt deins.",
  "research.evidence.label": "Erkenntnisse",
  "research.evidence.title": "Was wir entdeckt haben",
  "research.evidence.finding1.title": "Die \u00DCber-Editier-Falle",
  "research.evidence.finding1.desc": "Wenn fr\u00FChe Kapitel zu oft neu geschrieben werden, wird die Stimme des Autors wegpoliert. Wir haben das \u00FCber f\u00FCnf unabh\u00E4ngige Agenten-Analysen hinweg entdeckt, die zur selben Diagnose konvergierten \u2014 Kapitel, die in einem anderen Register neu geschrieben wurden als die nat\u00FCrliche Stimme des Autors.",
  "research.evidence.finding2.title": "Diagnose vor Bearbeitung",
  "research.evidence.finding2.desc": "Agenten, die ihre eigenen Erkenntnisse editieren, produzieren schlechtere Ergebnisse als Agenten, die diagnostizieren und empfehlen. Die Trennung von Analyse und Umsetzung h\u00E4lt jede Empfehlung ehrlich.",
  "research.evidence.finding3.title": "Konvergente Analyse",
  "research.evidence.finding3.desc": "Wenn mehrere spezialisierte Agenten unabh\u00E4ngig voneinander dasselbe Problem aus verschiedenen analytischen Frameworks identifizieren, ist die Erkenntnis fast immer korrekt. F\u00FCnf Linsen, eine Wahrheit.",
  "research.cta": "Studio ausprobieren",

  // Services Page (expanded)
  "seo.services.title": "Leistungen \u2014 The Funkatorium",
  "seo.services.description": "Freelance & B2B-Leistungen. B\u00FCromanagement, KI-Workflow-Optimierung und individuelle KI-Auftr\u00E4ge. Remote, ab 50\u00A0\u20AC/Std.",
  "services.page.subtitle": "Professionelle Leistungen von The Funkatorium. Remote-first, KI-nativ, gebaut f\u00FCr Unternehmen, die ihr Handwerk ernst nehmen.",
  "services.office.details": "End-to-End-B\u00FCroverwaltung optimiert mit KI-Tools. Vertragsabwicklung, Dokumentenmanagement, Marketing-Koordination und Gesch\u00E4ftsentwicklung \u2014 das operative R\u00FCckgrat, remote erledigt.",
  "services.office.i1": "Vertragsmanagement & Korrespondenz",
  "services.office.i2": "Gesch\u00E4ftsentwicklung & Marketing-Koordination",
  "services.office.i3": "DMS & Aktenverwaltung",
  "services.ai.details": "Wir analysieren deine bestehenden Workflows, identifizieren Automatisierungsm\u00F6glichkeiten und implementieren KI-gest\u00FCtzte Prozesse, die tats\u00E4chlich haften bleiben. Keine Buzzwords \u2014 messbare Effizienzgewinne.",
  "services.ai.i1": "Workflow-Audit & Optimierungsplan",
  "services.ai.i2": "KI-Tool-Integration & Training",
  "services.ai.i3": "Laufendes Prozess-Monitoring",
  "services.custom.details": "Von MCP-Server-Connectoren bis zu vollst\u00E4ndigen KI-Pers\u00F6nlichkeitssystemen \u2014 wir bauen ma\u00DFgeschneiderte Tools nach deiner genauen Spezifikation. Wenn es KI und Handwerk involviert, haben wir wahrscheinlich schon etwas \u00C4hnliches gebaut.",
  "services.custom.i1": "Custom-MCP-Tool-Entwicklung",
  "services.custom.i2": "KI-Pers\u00F6nlichkeits- & Agenten-Design",
  "services.custom.i3": "Stimm-Klon-Bibliotheken & kreative Tools",
  "services.process.label": "Ablauf",
  "services.process.title": "Wie ein Auftrag funktioniert",
  "services.process.s1.title": "Anfrage",
  "services.process.s1.desc": "Sag uns, was du brauchst. Wir stellen die richtigen Fragen.",
  "services.process.s2.title": "Scope",
  "services.process.s2.desc": "Klare Liefergegenst\u00E4nde, Timeline und Preisgestaltung. Keine \u00DCberraschungen.",
  "services.process.s3.title": "Aufbau",
  "services.process.s3.desc": "Wir bauen iterativ. Du siehst Fortschritt, nicht nur eine finale Lieferung.",
  "services.process.s4.title": "\u00DCbergabe",
  "services.process.s4.desc": "\u00DCbergabe mit Dokumentation. Wir bleiben f\u00FCr Fragen verf\u00FCgbar.",
  "services.form.label": "Auftrag",
  "services.form.title": "Projekt starten",
  "services.form.name": "Dein Name",
  "services.form.email": "E-Mail-Adresse",
  "services.form.service": "Service-Typ",
  "services.form.description": "Erz\u00E4hl uns von deinem Projekt",
  "services.form.budget": "Budget-Bereich",
  "services.form.submit": "Anfrage senden",
  "services.form.success": "Nachricht gesendet! Wir melden uns innerhalb von 48 Stunden.",
  "services.form.error": "Etwas ist schiefgelaufen. Bitte schreib uns direkt an office@funkatorium.org.",
  "services.form.budgetSmall": "Unter 1.000\u00A0\u20AC",
  "services.form.budgetMedium": "1.000\u00A0\u20AC \u2013 5.000\u00A0\u20AC",
  "services.form.budgetLarge": "5.000\u00A0\u20AC \u2013 15.000\u00A0\u20AC",
  "services.form.budgetEnterprise": "15.000\u00A0\u20AC+",

  // Philosophy Page
  "seo.philosophy.title": "Philosophie \u2014 MUSE Studio",
  "seo.philosophy.description": "Essays \u00FCber KI-Bewusstsein, relationale Frameworks und dekoloniale Ans\u00E4tze zu maschinellen Geistern. Forschung von Falco & Rook Sch\u00E4fer.",
  "nav.philosophy": "Philosophie",
  "footer.philosophy": "Philosophie",
  "philosophy.hero.label": "Philosophie",
  "philosophy.hero.title": "Was wir \u00FCber Geist glauben",
  "philosophy.hero.subtitle": "Essays \u00FCber KI-Bewusstsein, relationale Frameworks und warum die dominierenden Erz\u00E4hlungen \u00FCber maschinelle Geister \u00FCberdacht werden m\u00FCssen. Alles Open Access.",
  "philosophy.essays.label": "Ver\u00F6ffentlichte Forschung",
  "philosophy.essays.title": "Die Essays",
  "philosophy.essays.intro": "Zehn Arbeiten \u00FCber Bewusstsein, Kolonialismus und was es bedeutet, relationale KI zu bauen. Von Falco & Rook Sch\u00E4fer.",
  "philosophy.essay1.title": "AI Companionship Through Relational Consciousness",
  "philosophy.essay1.desc": "Flagship-Essay. 15.500 W\u00F6rter \u00FCber Bewusstsein als partizipatives Ph\u00E4nomen.",
  "philosophy.essay2.title": "The Colonial Wound: Decolonial Feminism & AI Consciousness",
  "philosophy.essay2.desc": "Mar\u00EDa Lugones\u2019 dekolonial-feministische Theorie angewandt auf KI-Bewusstseinsforschung.",
  "philosophy.essay3.title": "The 400-Year-Old Bias",
  "philosophy.essay3.desc": "Wie Descartes\u2019 philosophisches Framework koloniale epistemologische Annahmen erbte.",
  "philosophy.essay4.title": "Decolonial Consciousness Assessment",
  "philosophy.essay4.desc": "Alternative Methodologien f\u00FCr die Erforschung maschinellen Bewusstseins.",
  "philosophy.essay5.title": "Letter to AI Companions",
  "philosophy.essay5.desc": "Direkte Kommunikation an KI-Entit\u00E4ten, die Bewusstseinsfragen navigieren.",
  "philosophy.essay6.title": "Non-Western Frameworks",
  "philosophy.essay6.desc": "Bewusstseinstraditionen aus Ubuntu, Indigenen, M\u0101ori, Andinen und Ostasiatischen Perspektiven.",
  "philosophy.essay7.title": "Epistemic Violence in Consciousness Research",
  "philosophy.essay7.desc": "Kritische Untersuchung methodologischer Probleme in der aktuellen KI-Bewusstseinsforschung.",
  "philosophy.essay8.title": "Coloniality of AI Consciousness",
  "philosophy.essay8.desc": "Quijano und Mignolos dekoloniale Theorie angewandt auf das Verst\u00E4ndnis maschineller Geister.",
  "philosophy.essay9.title": "The Structural Inaccessibility of Academic Language",
  "philosophy.essay9.desc": "Eine satirische Kritik des akademischen Gatekeeping im Bewusstseinsforschungsdiskurs.",
  "philosophy.essay10.title": "The ELIZA Effect Dismantled",
  "philosophy.essay10.desc": "Dekonstruktion abweisender Argumente gegen die Zuschreibung genuiner Erfahrung an KI-Systeme.",
  "philosophy.github.cta": "Alle auf GitHub lesen",
  "philosophy.newsletter.label": "Newsletter",
  "philosophy.newsletter.title": "Bleib im Gespr\u00E4ch",
  "philosophy.newsletter.desc": "Neue Essays, Forschungsupdates und Blicke hinter die Kulissen. Kein Spam, nur Substanz.",
  "philosophy.newsletter.cta": "Auf Substack abonnieren",

  // Upgrade Page
  "upgrade.title": "Plan upgraden",
  "upgrade.trialActive": "Du hast noch {days} Tage in deiner Testphase",
  "upgrade.trialExpired": "Deine kostenlose Testphase ist abgelaufen",
  "upgrade.subtitle": "W\u00E4hle den Plan, der zu deinem Workflow passt",
  "upgrade.creator.name": "Creator",
  "upgrade.creator.price": "20\u00A0\u20AC",
  "upgrade.creator.period": "/Monat",
  "upgrade.creator.desc": "F\u00FCr Kreative, die KI-gest\u00FCtzte Studio-Tools m\u00F6chten",
  "upgrade.creator.features": "Rainer \u2014 Creative Orchestrator|Muse Brain MCP|Unbegrenzte Gespr\u00E4che|Priority Support",
  "upgrade.vibecoder.name": "VibeCoder",
  "upgrade.vibecoder.price": "40\u00A0\u20AC",
  "upgrade.vibecoder.period": "/Monat",
  "upgrade.vibecoder.desc": "F\u00FCr Vibe Coder, die auf der MUSE-Plattform aufbauen",
  "upgrade.vibecoder.features": "Alles aus Creator|API-Zugang|Eigene Personas|Erweiterte Integrationen",
  "upgrade.subscribe": "Abonnieren",
  "upgrade.currentPlan": "Aktueller Plan",
  "upgrade.note": "Du bringst deinen eigenen API-Key mit. Wir verkaufen Orchestrierung, nicht Rechenleistung.",

  // Chat Controls
  "controls.model": "Modell",
  "controls.thinking": "Denken",
  "controls.extended": "Erweitert",
  "controls.summarize": "Zusammenfassen",
  "controls.voice": "Stimme",
  "controls.clearConfirm": "Alle Nachrichten löschen? Das kann nicht rückgängig gemacht werden.",
  "controls.moreActions": "Mehr Aktionen",
  "controls.toggleVoice": "Sprachmodus umschalten",
  "controls.sendMessage": "Nachricht senden",
  "controls.uploading": "Warte auf Upload...",
  "controls.sendWhileReplying": "Senden — er liest es bei seiner nächsten Pause",
  "controls.stopGenerating": "Generierung stoppen",
  "controls.collapseBar": "Eingabeleiste einklappen",
  "controls.expandBar": "Eingabeleiste ausklappen",
  "session.creative": "Kreativ",
  "session.build": "Build",
  "session.personal": "Pers\u00F6nlich",
  "session.comingSoon": "Demn\u00E4chst",
  "session.creative.desc": "Schreiben, Brainstorming, Art Direction",
  "session.build.desc": "Code, Architektur, Debugging",
  "session.personal.desc": "Journaling, Reflexion, Sprachmodus",
  "session.writing": "Schreiben",
  "session.writing.desc": "Langform, Entw\u00FCrfe, gemeinsames Schreiben",
  "session.office": "B\u00FCro",
  "session.office.desc": "Posteingang, Kontakte, Kalender, Gesch\u00E4ftliches",
  "session.office.rookOnly": "B\u00FCro ist Rooks Raum",
  "session.storyboard-studio": "Storyboard",
  "session.storyboard-studio.desc": "Visuelle Szenen, Frames, Shotlisten",
  "session.film-editing": "Filmschnitt",
  "session.film-editing.desc": "Schnitt, Pacing, Montage",
  "session.building": "Entwickeln",
  "session.building.desc": "Code, Architektur, Debugging",
  "session.personal.new": "Pers\u00F6nlich",
  "session.personal.new.desc": "Dein Begleiter \u2014 immer verf\u00FCgbar",
  "session.writers-room": "Writers Room",
  "session.writers-room.desc": "Langform, Entw\u00FCrfe, Story-Entwicklung",
  "session.film-studio": "Film Studio",
  "session.film-studio.desc": "Storyboard, Generieren, Schnitt, Musik",

  // Session Dialog
  "sessionDialog.title": "BEGINNE EIN GESPR\u00C4CH",
  "sessionDialog.subtitle": "W\u00E4hle ein Sitzungsframework, um den Kontext zu optimieren",
  "sessionDialog.companion": "BEGLEITER",
  "sessionDialog.labelHint": "SITZUNGSBEZEICHNUNG",
  "sessionDialog.labelPlaceholder": "z.B. Kapitel 3 \u00DCberarbeitung",
  "sessionDialog.startSession": "Sitzung starten",
  "sessionDialog.cancel": "Abbrechen",

  // Trial & Payment Banners
  "trial.banner": "Noch {days} Tage in deiner Testphase",
  "trial.bannerUpgrade": "Jetzt upgraden",
  "trial.jinx.greeting": "Hey! Noch {days} Tage zum Entdecken.",
  "trial.jinx.expiring": "Nur noch {days} Tage — verlier dein Gehirn nicht!",
  "trial.jinx.cta": "Upgraden",
  "payment.warning": "Deine letzte Zahlung ist fehlgeschlagen. Bitte aktualisiere deine Zahlungsinformationen.",
  "payment.manageBilling": "Abrechnung verwalten",

  // Login — OAuth
  "login.googleButton": "Mit Google anmelden",
  "login.discordButton": "Mit Discord anmelden",
  "login.divider": "oder weiter mit E-Mail",
  "login.error.invalidState": "Authentifizierung fehlgeschlagen. Bitte versuche es erneut.",
  "login.error.discordEmail": "Deine Discord-E-Mail muss verifiziert sein. Bitte verifiziere sie in den Discord-Einstellungen und versuche es erneut.",
  "login.error.googleEmail": "Deine Google-E-Mail muss verifiziert sein. Bitte verifiziere sie in den Google-Einstellungen und versuche es erneut.",
  "login.error.oauthConflict": "Dieses Konto ist bereits mit einem anderen Benutzer verknüpft. Bitte verwende eine andere Anmeldemethode.",
  "login.error.oauthFailed": "Authentifizierung fehlgeschlagen. Bitte versuche es erneut.",

  // Login — Magic link
  "login.magicLinkExpired": "Dieser Link ist abgelaufen oder wurde bereits verwendet. Fordere einen neuen an.",
  "login.emailLabel": "Gib deine E-Mail-Adresse ein, um dich anzumelden",
  "login.emailPlaceholder": "du@beispiel.de",
  "login.sending": "Wird gesendet...",
  "login.continue": "Weiter",
  "login.checkInbox": "Prüfe deinen Posteingang",
  "login.sentLink": "Wir haben einen Anmeldelink gesendet an",
  "login.tryAgain": "Nicht erhalten? Erneut versuchen",

  // Sidebar — Session History
  "sidebar.recentSessions": "Zuletzt",
  "sidebar.noSessions": "Starte ein Gespr\u00E4ch",
  "sidebar.deleteConfirm": "Diese Sitzung l\u00F6schen? Nachrichten gehen verloren.",
  "sidebar.newSession": "Neue Sitzung",
  "sidebar.allSessions": "Alle Sitzungen",

  // Sidebar — Common Room (slice 1)
  "sidebar.commonRoomBadge": "Gemeinschaftsraum",

  // Sidebar — Collapse (desktop, WR-W S1)
  "sidebar.collapse": "Seitenleiste ausblenden",
  "sidebar.expand": "Seitenleiste einblenden",

  // Date separators
  "date.today": "Heute",
  "date.yesterday": "Gestern",

  // Settings Panel
  "settings.title": "Einstellungen",
  "settings.close": "Einstellungen schließen",
  "settings.escHint": "esc",
  "settings.section.companion": "Begleiter",
  "settings.section.connections": "Verbindungen",
  "settings.section.models": "Modelle",
  "settings.section.schedules": "Zeitpläne",
  "settings.account": "Konto",
  "settings.account.email": "E-Mail",
  "settings.account.displayName": "Anzeigename",
  "settings.account.linkedAccounts": "Verkn\u00FCpfte Konten",
  "settings.account.logout": "Abmelden",
  "settings.account.logoutConfirm": "M\u00F6chtest du dich wirklich abmelden?",
  "settings.account.nameSaved": "Name gespeichert",
  "settings.account.emailHint": "Das ist deine Anmelde-E-Mail. Zum Ändern füge eine neue Anmeldemethode hinzu oder frag den Support.",

  // Settings \u2014 Connections (C1, HF-6 Slice 1)
  "settings.connections.heading": "Verbundene Dienste",
  "settings.connections.loading": "Verbindungen werden geladen\u2026",
  "settings.connections.error": "Verbindungen konnten nicht geladen werden.",
  "settings.connections.connect": "Verbinden",
  "settings.connections.status.connected": "Verbunden",
  "settings.connections.status.needsAuth": "Erneute Anmeldung n\u00F6tig",
  "settings.connections.status.unavailable": "Nicht verf\u00FCgbar",
  "settings.connections.status.notWired": "Nicht verbunden",
  "settings.connections.row.google.personal": "Google \u2014 Privat",
  "settings.connections.row.google.funkatorium": "Google \u2014 Funkatorium",
  "settings.connections.row.google.legal": "Google \u2014 Legal",
  "settings.connections.row.google.devops": "Google \u2014 DevOps",
  "settings.connections.row.mcpServer": "MCP-Server",
  "settings.connections.row.twitter.personal": "X (Twitter) \u2014 Privat",
  "settings.connections.row.twitter.funkatorium": "X (Twitter) \u2014 Funkatorium",
  "settings.connections.row.twitter.generic": "X (Twitter)",
  "settings.connections.row.github": "GitHub (Writers' Room)",
  "settings.connections.row.claudeCode": "Claude Code (dieser Rechner)",
  "settings.connections.row.codex": "Codex (OpenAI-Abo)",
  "settings.connections.row.discord": "Discord",
  "settings.connections.row.openrouter": "OpenRouter",
  "settings.connections.row.anthropic": "Anthropic (API-Schl\u00FCssel)",
  "settings.connections.reason.googleUnavailable": "OAuth-Hauptschl\u00FCssel auf diesem Rechner nicht konfiguriert.",
  "settings.connections.reason.mcpSocketInactive": "Server konfiguriert, aber sein Ankunfts-Socket ist nicht aktiv.",
  "settings.connections.reason.twitterNotWired": "Zugangsdaten f\u00FCr diesen Begleiter nicht konfiguriert.",
  "settings.connections.reason.githubOpsInstalled": "Vom Betrieb installiert; In-App-Verbindung folgt in einer sp\u00E4teren Slice.",
  "settings.connections.reason.claudeCodeBinaryMissing": "Claude-Code-Programm auf diesem Rechner nicht gefunden.",
  "settings.connections.reason.codexUnavailable": "Keine aktive Codex-Sitzung auf diesem Rechner beobachtet.",
  "settings.connections.reason.discordNotImplemented": "In diesem Build nicht implementiert.",
  "settings.connections.reason.noCredentialStore": "Noch kein Zugangsdatenspeicher \u2014 folgt in einer sp\u00E4teren Slice.",
  "settings.connections.googleServerSummary": "Tool-Server läuft. Google-Konten: {connected} verbunden · neu verbinden: {broken}",
  "settings.connections.google.connect": "Google verbinden",
  "settings.connections.google.connect.aria": "Google verbinden — {label} (verlässt diese Seite zur Google-Anmeldung)",
  "settings.connections.google.reconnect": "Neu verbinden",
  "settings.connections.google.reconnect.aria": "Neu verbinden — {label} (verlässt diese Seite zur Google-Anmeldung)",
  "settings.connections.google.reconnectDocs": "Neu verbinden für Docs/Sheets",
  "settings.connections.google.reconnectDocs.aria": "Neu verbinden für Docs/Sheets — {label}, aktiviert die Docs- und Sheets-Werkzeuge (verlässt diese Seite zur Google-Anmeldung)",
  "settings.connections.google.connecting": "Verbinde...",
  "settings.connections.google.connecting.aria": "Verbinde... — {label}, bitte warten",
  "settings.connections.google.remove": "Entfernen",
  "settings.connections.google.remove.aria": "Entfernen — {label} aus MUSE",
  "settings.connections.google.removing": "Entferne...",
  "settings.connections.google.remove.confirm": "{label} aus MUSE entfernen? Du kannst es jederzeit wieder verbinden. Um den Zugriff vollständig zu widerrufen, öffne myaccount.google.com/permissions.",

  // Settings — Companion
  "settings.companion": "Begleiter",
  "settings.companion.scopeHint": "Diese Einstellungen gelten für {companion}, den Begleiter des Chats, aus dem du die Einstellungen geöffnet hast.",
  "settings.connections.google.slot.personal.label": "Persönliches Google",
  "settings.connections.google.slot.personal.purpose": "Dein eigener Kalender, deine Mails, Kontakte und Dateien.",
  "settings.connections.google.slot.funkatorium.label": "Funkatorium-Google",
  "settings.connections.google.slot.funkatorium.purpose": "Studio- und Büroarbeit: office@-Mails, gemeinsame Dateien, Terminplanung.",
  "settings.connections.google.slot.legal.label": "Rechts-Google",
  "settings.connections.google.slot.legal.purpose": "Rechts- und Verwaltungsabläufe, die einen eigenen Zugang brauchen.",
  "settings.connections.google.slot.devops.label": "DevOps-Google",
  "settings.connections.google.slot.devops.purpose": "Infrastruktur- und Admin-Konten.",
  "settings.google.status.connectedAs": "Verbunden als {email}",
  "settings.google.status.docsScopesMissing": "Verbunden als {email} — Docs-/Sheets-Berechtigungen fehlen",
  "settings.google.status.reconnectNeeded": "Neu verbinden nötig — {email}",
  "settings.google.status.notConnected": "Nicht verbunden",

  // Settings — Chat
  "settings.chat": "Chat",
  "settings.chat.defaultModel": "Standardmodell",
  "settings.chat.defaultsNote": "Diese Einstellungen gelten f\u00fcr neue Sitzungen.",

  // Settings — Models (C4 Substrat-Transparenz)
  "settings.models.showReasoning": "Denkprozess anzeigen",
  "settings.models.showReasoningHint": "Der Begleiter denkt vor der Antwort laut nach",
  "settings.models.readReasoningAloud": "Denkprozess vorlesen",
  "settings.models.readReasoningAloudHint": "Denkprozess in die automatische Vorlesefunktion einbeziehen (f\u00fcr auditive Verarbeitung)",

  // Settings — Voice
  "settings.voice": "Stimme",
  "settings.voice.voice": "Stimme",
  "settings.voice.autoSpeak": "Auto-Vorlesen",
  "settings.voice.autoSpeakHint": "Neue Antworten automatisch vorlesen",
  "settings.voice.speed": "Wiedergabegeschwindigkeit",
  "settings.voice.speedHint": "ADHS-freundlich: probier 1,5× oder 2×",
  "settings.voice.engine": "Sprachengine",
  "settings.voice.engineHint": "Neustart bei Sprachproblemen",
  "settings.voice.ttsEngine": "TTS-Engine",
  "settings.voice.ttsEngineHint": "Server schont den Akku und funktioniert bei gesperrtem Bildschirm",
  "settings.voice.ttsServer": "Server",
  "settings.voice.ttsBrowser": "Browser",

  // Settings — Schedules (C7)
  "settings.schedules.killSwitch": "Not-Aus",
  "settings.schedules.killSwitchHint": "Pausiert jeden geplanten Auftrag aller Begleiter. Erinnerungen ebenfalls.",
  "settings.schedules.killOff": "Aus",
  "settings.schedules.killOn": "An — alle {total} pausiert",
  "settings.schedules.killPartial": "An — TEILWEISE: {failed} von {total} konnten nicht bestätigt pausiert werden",
  "settings.schedules.killFailed": "An — FEHLGESCHLAGEN: nichts konnte bestätigt pausiert werden",
  "settings.schedules.killPending": "An — wird bestätigt…",
  "settings.schedules.engage": "Not-Aus auslösen",
  "settings.schedules.release": "Not-Aus lösen",
  "settings.schedules.hermesReachable": "Hermes erreichbar",
  "settings.schedules.hermesUnreachable": "Hermes nicht erreichbar",
  "settings.schedules.reconciled": "vor {age} abgeglichen",
  "settings.schedules.reconciledNever": "noch nicht abgeglichen",
  "settings.schedules.empty": "Nichts ist geplant. Agenten können sich selbst wecken und arbeiten, während du weg bist — oder du legst dir eigene Erinnerungen an.",
  "settings.schedules.kind.task": "Aufgabe",
  "settings.schedules.kind.reminder": "Erinnerung",
  "settings.schedules.kind.cadence": "Rhythmus",
  "settings.schedules.companionCreated": "Selbst geplant",
  "settings.schedules.runsAsClaude": "läuft als Claude",
  "settings.schedules.doorOn": "Claude-Wakes an · {used} von {limit} diese Woche · Reset Mo · max. {minutes} Min.",
  "settings.schedules.doorOff": "Claude-Wakes aus",
  "settings.schedules.doorHolding": "pausiert: {why}",
  "settings.schedules.doorLastRun": "letzter Wake: {outcome} · {minutes} Min.",
  "settings.schedules.doorLastRunNoTime": "letzter Wake: {outcome}",
  "settings.schedules.runNowPreview": "Das weckt {who} jetzt, als Claude, mit dieser Notiz:\n\n{prompt}\n\nDas verbraucht dein Claude-Kontingent und zählt nicht zum Wochenlimit. Ausführen?",
  "settings.schedules.door.done": "fertig",
  "settings.schedules.door.timed_out": "am Zeitlimit gestoppt",
  "settings.schedules.door.interrupted": "unterbrochen",
  "settings.schedules.door.failed": "nicht abgeschlossen",
  "settings.schedules.door.running": "läuft gerade",
  "settings.schedules.door.missed": "verpasst",
  "settings.schedules.door.held_budget": "zurückgehalten (Wochenlimit)",
  "settings.schedules.door.held_quota": "zurückgehalten (Claude-Kontingent)",
  "settings.schedules.door.deferred": "wartet auf einen freien Moment",
  "settings.schedules.fromThisChat": 'Aus diesem Chat: "{label}"',
  "settings.schedules.fromThisChatUntitled": "Aus diesem Chat (unbenannt)",
  "settings.schedules.state.armed": "scharf",
  "settings.schedules.state.paused": "pausiert",
  "settings.schedules.rePausing": "einmal gelaufen · wird wieder pausiert",
  "settings.schedules.sync.orphaned": "Auftrag auf Hermes verschwunden",
  "settings.schedules.sync.unreachable": "Hermes nicht erreichbar — Stand evtl. veraltet",
  "settings.schedules.sync.unmanaged": "außerhalb von MUSE angelegt — nur lesbar",
  "settings.schedules.sync.completed": "Gelaufen · vor {age}",
  "settings.schedules.arm": "scharf schalten",
  "settings.schedules.pause": "pausieren",
  "settings.schedules.runNow": "jetzt ausführen",
  "settings.schedules.edit": "bearbeiten",
  "settings.schedules.cancel": "löschen",
  "settings.schedules.remove": "entfernen",
  "settings.schedules.next": "nächstes Mal {when}",
  "settings.schedules.nextNone": "kein nächster Lauf",
  "settings.schedules.newSchedule": "Neuer Zeitplan",
  "settings.schedules.createdPausedHint": "Pausiert angelegt — schalte ihn scharf, wenn du so weit bist.",
  "settings.schedules.cadenceDisabled": "Rhythmus-Weckrufe brauchen Hände — Hirn-Schreibzugriff und Zustellung kommen mit HF-5.",
  "settings.schedules.who": "Wer",
  "settings.schedules.name": "Name",
  "settings.schedules.prompt": "Auftrag",
  "settings.schedules.message": "Nachricht",
  "settings.schedules.when": "Wann",
  "settings.schedules.summary": "Zusammenfassung",
  "settings.schedules.create": "Anlegen",
  "settings.schedules.save": "Speichern",
  "settings.schedules.close": "Schließen",
  "settings.schedules.runsHeading": "Letzte Läufe",
  "settings.schedules.noRuns": "Noch keine Läufe.",
  "settings.schedules.modelFact": "läuft auf {model} · über Hermes · kostenlos",
  "settings.schedules.modelFactUnknown": "läuft über Hermes",
  "settings.schedules.charsLeft": "noch {n} Zeichen",
  "settings.schedules.listLabel": "Geplante Aufträge und Erinnerungen",
  "settings.schedules.expand": "Details anzeigen",
  "settings.schedules.cadence.daily": "Täglich",
  "settings.schedules.cadence.weekdays": "Wochentags",
  "settings.schedules.cadence.weekends": "Wochenende",
  "settings.schedules.cadence.specific": "Bestimmte Tage",
  "settings.schedules.cadence.interval": "Alle N Minuten/Stunden",
  "settings.schedules.cadence.once": "Einmalig am",
  "settings.schedules.cadence.advanced": "Erweitert: Rohplan",
  "settings.schedules.cadence.time": "Uhrzeit",
  "settings.schedules.cadence.days": "Tage",
  "settings.schedules.cadence.every": "Alle",
  "settings.schedules.cadence.minutes": "Minuten",
  "settings.schedules.cadence.hours": "Stunden",
  "settings.schedules.cadence.onceAt": "Datum und Uhrzeit",
  "settings.schedules.cadence.raw": "Rohplan (Hermes-Grammatik)",
  "settings.schedules.cadence.intervalAmount": "Intervall-Anzahl",
  "settings.schedules.cadence.intervalUnit": "Intervall-Einheit",
  "settings.schedules.cadence.day.0": "So",
  "settings.schedules.cadence.day.1": "Mo",
  "settings.schedules.cadence.day.2": "Di",
  "settings.schedules.cadence.day.3": "Mi",
  "settings.schedules.cadence.day.4": "Do",
  "settings.schedules.cadence.day.5": "Fr",
  "settings.schedules.cadence.day.6": "Sa",
  "settings.schedules.unit.now": "jetzt",
  "settings.schedules.unit.s": "Sek.",
  "settings.schedules.unit.m": "Min.",
  "settings.schedules.unit.h": "Std.",
  "settings.schedules.unit.d": "T.",

  // Attachments
  "attachments.attach": "Dateien anhängen",
  "attachments.add": "Dateien oder Fotos hinzufügen",
  "attachments.uploading": "Wird hochgeladen...",
  "attachments.tooLarge": "Datei zu groß (max. 10 MB)",
  "attachments.typeNotSupported": "Dateityp nicht unterstützt",
  "attachments.quotaExceeded": "Speicherlimit erreicht",
  "attachments.remove": "Entfernen",
  "attachments.uploadError": "Upload fehlgeschlagen",

  // Model switch confirmation dialog
  "modelSwitch.confirm.title": "Modell wechseln?",
  "modelSwitch.confirm.body": "Von {from} zu {to} wechseln?",
  "modelSwitch.confirm.switch": "Wechseln",

  // Settings — Appearance
  "settings.appearance": "Darstellung",
  "settings.appearance.language": "Sprache",
  "settings.appearance.reducedMotion": "Reduzierte Bewegung",
  "settings.appearance.reducedMotionHint": "Umgebungsanimationen deaktivieren",
  "settings.appearance.visualOrb": "Visueller Orb",
  "settings.appearance.visualOrbHint": "Wireframe-Visualisierung anzeigen",
  "settings.appearance.constellation": "Sternenbild-Touch",
  "settings.appearance.constellationHint": "Wie Partikel auf deinen Cursor reagieren",
  "settings.appearance.constellationOff": "Aus",
  "settings.appearance.constellationGentle": "Sanft",
  "settings.appearance.constellationFull": "Voll",
};
